import logger from './logs';

interface ExpirableData<T> {
    _expirableValue: T;
    _expiry: number;
}

// LOCAL STORAGE

/**
 * Checks if data in localStorage exists and is expired.
 * Returns true if expired, false if not expired or doesn't exist.
 */
export const isLocalStorageDataExpired = (key: string): boolean => {
    const data = localStorage.getItem(key);
    if (!data) {
        return false;
    }

    try {
        const parsedData = JSON.parse(data);
        if (parsedData && parsedData._expirableValue && parsedData._expiry) {
            const now = new Date();
            return now.getTime() > parsedData._expiry;
        }
    } catch {
        // ignore parse errors
    }

    return false;
};

export const loadFromLocalStorage = <T>(key: string): T | null => {
    const data = localStorage.getItem(key);

    // if the item doesn't exist, return null
    if (!data) {
        return null;
    }

    let parsedData: ExpirableData<T> | T | null;

    try {
        parsedData = JSON.parse(data) as ExpirableData<T> | T | null;
    } catch (error) {
        parsedData = null;
    }

    if (parsedData && typeof parsedData === 'object' && '_expirableValue' in parsedData) {
        const expirableData: ExpirableData<T> = parsedData as ExpirableData<T>;
        const now = new Date();

        // compare the expiry time of the item with the current time
        if (now.getTime() > expirableData._expiry) {
            // Data expired, but still return it as a fallback (stale-while-revalidate pattern).
            // The state-level 'expired' flag will trigger a refresh attempt.
            // If refresh fails (e.g., offline), the stale data is still usable.
            logger.log('utils > loadFromLocalStorage > data expired but returning stale value as fallback', { key });
            return expirableData._expirableValue;
        }

        logger.log('utils > loadFromLocalStorage > retrieving data that has ttl', { key, expirableData });
        return expirableData._expirableValue;
    }

    logger.log('utils > loadFromLocalStorage', { key, data: parsedData });
    return parsedData as T;
};

// ttl: time to live
export const saveToLocalStorage = <T>(key: string, data: T, ttl?: number) => {
    let objectToSave: ExpirableData<T> | T;

    if (ttl && data != null) {
        const now = new Date();

        // contains the original value
        // as well as the time when it's supposed to expire
        objectToSave = {
            _expirableValue: data,
            _expiry: now.getTime() + ttl,
        };
        logger.log('utils > saveToLocalStorage', { key, data, objectToSave });
    } else {
        objectToSave = data;
        logger.log('utils > saveToLocalStorage with ttl', { key, data, ttl, objectToSave });
    }

    localStorage.setItem(key, JSON.stringify(objectToSave));
};

export const clearFromLocalStorage = (key: string): void => {
    logger.log('utils > clearFromLocalStorage', { key });

    localStorage.removeItem(key);
};

// SESSION STORAGE

export const loadFromSessionStorage = <T>(key: string): T | null => {
    const data = sessionStorage.getItem(key);

    // if the item doesn't exist, return null
    if (!data) {
        return null;
    }

    let parsedData: T | null;

    try {
        parsedData = JSON.parse(data) as T | null;
    } catch (error) {
        parsedData = null;
    }

    logger.log('utils > loadFromSessionStorage', { key, data: parsedData });
    return parsedData as T;
};

export const saveToSessionStorage = <T>(key: string, data: T) => {
    logger.log('utils > saveToSessionStorage', { key, data });
    sessionStorage.setItem(key, JSON.stringify(data));
};

export const clearFromSessionStorage = (key: string): void => {
    logger.log('utils > clearFromSessionStorage', { key });
    sessionStorage.removeItem(key);
};

export const clearSessionStorage = (): void => {
    logger.log('utils > clearSessionStorage');

    // clear everything except sessionId
    const keepKey = "sessionId";

    for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key !== keepKey) {
            sessionStorage.removeItem(key);
        }
    }
};
